import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { Input, InputContainer, LabelStyle, TextArea } from './InputText.styles';

export type Props = InputHTMLAttributes<HTMLInputElement> & {
  active?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  field?: {
    /**
     * Takes precedence over `name` for the input's default ID and label target.
     */
    name?: string;
  };
  invalid?: boolean;
  isLabel?: boolean;
  label?: ReactNode | string;
  /**
   * Sets the input's default ID and label target when `field.name` is absent.
   */
  name?: string;
  ref?: any;
  settings?: Record<string, any>;
  type?: string;
  value?: any;
  variant?: string;
};

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  active?: boolean;
  field?: {
    /**
     * Sets the textarea's default ID and label target.
     */
    name?: string;
  };
  invalid?: boolean;
  label?: ReactNode | string;
  /**
   * Sets the textarea's HTML name attribute, not its label target.
   */
  name?: string;
  type?: InputType;
  variant?: string;
};

interface InputTextAreaProps extends Pick<Props, 'ref'>, TextAreaProps {}

type InputType = 'hidden' | 'number' | 'password' | 'tel' | 'text';

export const InputTextArea = forwardRef<HTMLTextAreaElement, InputTextAreaProps>(
  ({ active, field = { name: '' }, invalid = false, label = '', variant, ...props }, ref) => {
    const { name } = field;

    return (
      <>
        {label ? (
          <LabelStyle htmlFor={name} invalid={invalid}>
            {label}
          </LabelStyle>
        ) : null}
        <TextArea
          id={name}
          ref={ref}
          {...field}
          $active={active}
          $invalid={invalid}
          $label={label}
          $variant={variant}
          aria-invalid={invalid}
          {...props}
          className={undefined}
        />
      </>
    );
  }
);

const InputText = forwardRef<HTMLInputElement, Props>(
  (
    {
      active,
      className,
      disabled,
      field = { name: undefined },
      invalid = false,
      isLabel,
      label = '',
      name,
      placeholder,
      settings,
      type = 'text',
      variant,
      ...props
    },
    ref
  ) => (
    <InputContainer className={className} onChange={undefined}>
      {label && type !== 'hidden' ? (
        <LabelStyle htmlFor={field?.name || name} invalid={invalid}>
          {label}
        </LabelStyle>
      ) : null}
      <Input
        $active={active}
        $invalid={invalid}
        $isLabel={!!label}
        $variant={variant}
        aria-disabled={disabled}
        aria-invalid={invalid}
        disabled={disabled}
        id={field?.name || name}
        {...field}
        placeholder={placeholder}
        type={type}
        {...props}
        {...settings}
        className={undefined}
        ref={ref}
      />
    </InputContainer>
  )
);

export default InputText;
